<script setup lang="ts">
import ContactUs from '~/components/custom-ui/ContactUs.vue'
import { Button } from '@/components/ui/button'

useHead(
    {
      title: 'About Us - YourService.ae',
      meta: [
        {
          name: 'description',
          content: 'Learn more about YourService.ae, the leading platform connecting job seekers and employers in the UAE. Discover how we help you find the perfect job or the ideal candidate.'
        },
        {
          property: 'og:title',
          content: 'About Us - YourService.ae'
        },
        {
          property: 'og:description',
          content: 'Learn more about YourService.ae, the leading platform connecting job seekers and employers in the UAE. Discover how we help you find the perfect job or the ideal candidate.'
        }
      ]
    }
)

const {locales, locale} = useI18n();
const currentLanguage = computed(() => {
  return locales.value.find(item => item.code === locale.value)
})

const setStyle = computed(() => ({
  transform: currentLanguage.value.code === 'ar' ? 'scaleX(-1)' : 'none',
}));

</script>

<template>
  <div class="container mx-auto px-4" style="height: 100%">
    <div class="xl:min-w-[100vh]" style="display: flex; flex-direction: column;">
      <div style="flex-grow: 1;">
        <div class="my-10 flex items-center justify-center">
          <div class="ys-home-card w-full max-w-[1312px] h-full py-7 xl:max-h-[311px] flex items-center justify-center">
            <div class="lg:grid hidden place-items-center mr-auto ">
              <NuxtImg :style="setStyle" src="/home_page/lineart-2.png" alt="" style="object-fit: cover; height: 350px; object-position: right;"/>
            </div>
            <div>
              <h1 class="ys-home-text mb-4" style="text-align: center">{{ $t("ABOUT US") }}</h1>

              <div class="w-full max-w-[750px] xl:max-h-[154px] text-center px-5 xl:px-0">
                {{
                  $t("YourService is an online platform that connects users with specialists across various fields, enabling")
                }}
              </div>
            </div>
            <div class="lg:grid hidden place-items-center ml-auto">
              <NuxtImg :style="setStyle" src="/home_page/lineart.png" alt="" style="object-fit: cover ; height: 350px; object-position: left;"/>
            </div>
          </div>
        </div>
        <div class="gap-10">
          <img class="mx-auto" src="/d9ae94a4995c5873c66959adfb8f68bc.gif" alt=""
               style="object-fit: cover; height: 250px">
          <div class="flex justify-center items-center mb-5">
            <Button class="ys-tag-about ys-tag-about-blue-dark">{{ $t("Our Mission") }}</Button>
          </div>

          <div class="flex justify-center items-center flex-wrap mb-10">
            <div class="text-style-line lg:mr-4 mb-4 lg:mb-0"></div>
            <div class="w-[800px] lg:h-[110px] text-center my-1">
              {{ $t("At YourService, our mission is to empower individuals and businesses") }}
            </div>
            <div class="text-style-line mt-4 lg:ml-4 lg:mt-0"></div>
          </div>

          <div class="flex justify-center items-center  mb-10 text-center flex-wrap">
            <h1 class="ys-about-text">{{ $t("How to") }}
              <span class="ys-home-custom-text"> {{ $t("Find") }}</span>
              {{ $t("a Specialist") }} <br> {{ $t("on YourService?") }}
            </h1>
          </div>

          <div class="flex justify-center items-center gap-8 text-center flex-wrap">
            <div class="ys-about-card ys-about-card-green-light ">
              <Button class="ys-tag-about mb-4 ">
                {{ $t("Log In or Create an Account") }}
                <img src="/about/login.png" alt="" width="24">
              </Button>
              <p style="font-size: 14px">{{
                  $t("If you already have an account, click the *Login* button at the top right corner and enter your credentials.")
                }}<br>
                {{
                  $t("If you are new to YourService, click the *Register* button and follow the instructions to create a new account.")
                }}</p>

            </div>
            <div class="ys-about-card ys-about-card-red-light ">
              <Button class="ys-tag-about  mb-4">{{ $t("Use the Search Bar") }}<img src="/about/search.png" alt=""
                                                                                      width="24"></Button>
              <p style="font-size: 14px">{{
                  $t("On the homepage, you will find a search bar. Type in the type of service or specialist you are looking")
                }}</p>
            </div>
            <div class="ys-about-card ys-about-card-yellow-light">
              <Button class="ys-tag-about  mb-4">{{ $t("Filter Your Results") }}<img src="/about/adjustment.png"
                                                                                       alt="" width="24"></Button>
              <p style="font-size: 14px">{{
                  $t("Once the search results are displayed, you can use various filters to narrow your options. Filters may")
                }}</p>
            </div>
          </div>

          <div class="flex justify-center flex-wrap items-center gap-6 my-10 text-center">
            <div class="ys-about-card ys-about-card-purple-light w-full lg:w-[631px]">
              <Button class="ys-tag-about  mb-4">{{ $t("Browse Specialist Profiles") }}</Button>
              <img class="mx-auto -mt-14" src="/about/about_group.png" alt="" style="width: 600px">
              <p style="font-size: 14px">{{
                  $t("Browse through the list of specialists that match your criteria.")
                }}</p>
            </div>
            <div class="ys-about-card ys-about-card-blue-light w-full lg:w-[631px]">
              <Button class="ys-tag-about mb-1">{{ $t("Contact the Specialist") }}</Button>
              <img class="mx-auto" src="/about/techny-oval-speech-bubble-1%201.png" alt="" width="100">
              <p style="font-size: 14px">{{
                  $t("If you find a specialist you are interested in, click the *Contact* or *Hire* button on their profile.")
                }}</p>
            </div>
          </div>


          <div class="flex justify-center items-center my-10 text-center gap-8 flex-wrap">
            <div class="ys-about-card ys-about-card-orange-light w-full lg:w-1/3">
              <Button class="ys-tag-about mb-1 ">{{ $t("Discuss Your Needs") }}</Button>
              <img class="mx-auto" src="/about/bloom-chat-conversation%201.png" alt="" width="120">
              <p style="font-size: 14px">{{
                  $t("Once you have made contact, discuss your specific needs, availability, and any other details to ensure")
                }}</p>

            </div>
            <div class="ys-about-card ys-about-card-pink-light  w-full lg:w-1/3">
              <Button class="ys-tag-about  mb-1">{{ $t("Confirm and Hire") }}</Button>
              <img class="mx-auto" src="/about/rondy-stickers-lettering-yes-1%201.png" alt="" width="113">
              <p style="font-size: 14px">{{
                  $t("If you are satisfied with the discussion, finalize the agreement and hire the specialist through the")
                }}</p>
            </div>
            <div class="ys-about-card ys-about-card-green-light w-full lg:w-1/3">
              <Button class="ys-tag-about  mb-1">{{ $t("Provide Feedback") }}</Button>
              <img class="mx-auto" src="/about/bubble-gum-yellow-star-1%201.png" alt="" width="84">
              <p style="font-size: 14px">{{
                  $t("After completing the service, consider leaving a review for the specialist to help future users make")
                }}</p>
            </div>
          </div>

          <div class="flex justify-center items-center my-10 text-center">
            <h1 class="ys-about-text">{{ $t("Who is Responsible for") }} <br>
              <span class="ys-home-custom-text"> {{ $t("Work Quality") }}</span>
              {{ $t("on YourService?") }}
            </h1>
          </div>

          <div class="flex justify-center items-center">
            <div class="text-style-line mr-4" style="height: 45px"></div>
            <div class="w-[800px] h-[66px] text-center my-1">
              {{
                $t("At YourService, we are committed to connecting you with highly qualified specialists across various")
              }}
            </div>
            <div class="text-style-line ml-4" style="height: 45px"></div>
          </div>


          <div class="flex justify-center items-center gap-6 flex-wrap text-center my-10">
            <div class="flex lg:flex-col gap-6 flex-wrap">
              <div class="ys-about-card-gray w-full">
                <p class="ys-card-title-text">{{ $t("Specialists' Responsibility:") }}</p>
                <p class="my-4">{{
                    $t("Each specialist on YourService is an independent professional responsible for delivering high-quality")
                  }}</p>
              </div>
              <div class="ys-about-card-gray lg:mt-12 h-full lg:h-[188px] w-full">
                <p class="ys-card-title-text">{{ $t("Your Responsibility:") }}</p>
                <p class="my-4">{{
                    $t("As a client, you play a crucial role in the process. Communicate your requirements, expectations, and")
                  }}
                </p>
              </div>
            </div>
            <div class="ys-about-card-gray  !h-full lg:!h-[492px]">
              <p class="ys-card-title-text">{{ $t("YourService's Responsibility:") }}</p>
              <p class="my-4">{{
                  $t("While YourService does not directly manage or control the work performed by specialists, we strive to")
                }}</p>
              <ul class="text-start">
                <li class="my-1">○ {{
                    $t("Screening Specialists: We conduct thorough background checks and verify credentials to ensure we list")
                  }}
                </li>

                <li>○ {{
                    $t("Facilitating Communication: Our platform provides tools and resources to help you effectively")
                  }}
                </li>

                <li>○ {{
                    $t("Review and Rating System: We encourage clients to leave honest reviews and ratings. This feedback helps")
                  }}
                </li>

                <li>○ {{
                    $t("Support: Our customer support team is available to assist with any issues or disputes that may arise.")
                  }}
                </li>
              </ul>
            </div>
          </div>


          <div class="flex justify-center items-center">
            <div class="text-style-line mr-4 lg:h-[45px]"></div>
            <div class="w-full lg:w-[800px] lg:h-[66px] text-center my-1">
              {{
                $t("In summary, while the individual specialists are responsible for work quality, YourService is dedicated to")
              }}
            </div>
            <div class="text-style-line ml-4 lg:h-[45px]"></div>
          </div>


          <ContactUs/>

        </div>
      </div>
    </div>
  </div>
</template>


<style scoped lang="scss">

.ys-about-card-end {
  height: 261px;
  border-radius: 36px;
  background: rgba(247, 254, 248, 1);
}

.text-style-line {
  height: 65px;
  border: 1.48px solid rgba(45, 159, 117, 1);
}


.ys-tag-about {
  font-size: 17.74px !important;
  font-weight: 400;
  min-width: 125px;
  height: 36px;
  padding: 6px 12px 6px 12px;
  gap: 3px;
  border-radius: 28px;
  background: rgba(255, 255, 255, 1);
}

.ys-tag-about-blue-dark {
  color: rgba(45, 159, 117, 1);
  border: 1.48px solid rgba(45, 159, 117, 1);
}

.ys-about-card {
  max-width: 404px;
  width: 100%;
  top: 130px;
  padding: 24px 32px 24px 32px;
  gap: 30px;
  border-radius: 36px;
  @media(max-width:1024px){
    max-width: 100%;
  }
}

.ys-about-card-gray {
  width: 631px;
  height: 254px;
  top: 130px;
  padding: 24px 32px 24px 32px;
  gap: 30px;
  border-radius: 36px;
  background: rgba(246, 248, 250, 1);
  @media(max-width:1024px){
    width: 100%;
    height: auto;
  }
}

.ys-about-card-green-light {
    background: rgba(203, 245, 229, 0.5);

  .ys-tag-about {
    color: rgba(45, 159, 117, 1);
    border: 1.48px solid rgba(45, 159, 117, 1);
    background: rgba(203, 245, 229, 0.5);
  }
}

.ys-about-card-red-light {
    background: rgba(248, 201, 210, 0.5);

  .ys-tag-about {
    color: rgba(175, 29, 56, 1);
    border: 1.48px solid rgba(175, 29, 56, 1);
    background: rgba(248, 201, 210, 0.5);
  }
}


.ys-about-card-yellow-light {
    background: rgba(251, 223, 177, 0.5);
  .ys-tag-about {
    color: rgba(180, 120, 24, 1);
    border: 1.48px solid rgba(180, 120, 24, 1);
    background: rgba(251, 223, 177, 0.5);
  }
}

.ys-about-card-purple-light {
  .ys-tag-about {
    color: rgba(90, 54, 191, 1);
    border: 1.48px solid rgba(90, 54, 191, 1);
    background: rgba(202, 194, 255, 0.5);
  }
}

.ys-about-card-blue-light {
    background: rgba(194, 239, 255, 0.5);
  .ys-tag-about {
    color: rgba(45, 159, 117, 1);
    border: 1.48px solid rgba(45, 159, 117, 1);
    background: rgba(194, 239, 255, 0.5);
  }
}

.ys-about-card-orange-light {
    background: rgba(255, 218, 194, 0.5);
  .ys-tag-about {
    color: rgba(194, 84, 10, 1);
    border: 1.48px solid rgba(194, 84, 10, 1);
    background: rgba(255, 218, 194, 0.5);
  }
}

.ys-about-card-pink-light {
    background: rgba(249, 194, 255, 0.5);
  .ys-tag-about {
    color: rgba(156, 35, 169, 1);
    border: 1.48px solid rgba(156, 35, 169, 1);
    background: rgba(249, 194, 255, 0.5);
  }
}


.ys-about-text {
  font-family: Involve, sans-serif;
  color: rgba(0, 0, 0, 1);
  font-size: 34px;
  font-weight: 500;
  line-height: 34px;
  @media(max-width:768px){
    font-size: 21px;
  }
}


.ys-card-title-text {
  font-family: Involve, sans-serif;
  font-size: 24px;
  font-weight: 500;
  line-height: 31.92px;
  text-align: center;
  color: rgba(10, 13, 20, 1);
}

.ys-card-description-text {
  font-size: 18px;
  font-weight: 400;
  line-height: 21.78px;
  text-align: center;
  color: rgba(10, 13, 20, 1);
}

.ys-home-card {
  overflow: hidden;
  min-height: 356px;
  gap: 20px;
  border-radius: 36px;
  background: rgba(247, 254, 248, 1);
}


.ys-home-custom-text {
  font-family: Involve, sans-serif;
  font-style: italic;
  color: rgba(58, 62, 89, 1);
  font-size: 39px;
  font-weight: 500;
  line-height: 45px;
  @media(max-width:768px){
    font-size: 21px;
  }
}


</style>